/**
 * Terms and Conditions content.
 */
export const TERMS_MARKDOWN = `# Terms and Conditions

**Last updated: 26 February 2026**

These Terms and Conditions ("Terms") govern your use of the calling and voice services platform (the "Service") provided by **Void Inc.** ("we", "us", "our"), a company headquartered in Brisbane, Queensland, Australia. By creating an account or using the Service, you agree to these Terms. If you are using the Service on behalf of a business, you represent that you have authority to bind that business.

---

## 1. Eligibility

You must be at least 18 years old and have the legal capacity to enter into a binding contract. You must not use the Service if you are prohibited by applicable law or if your account has been suspended or terminated by us. The Service is intended for users in Australia.

---

## 2. Account and security

You must provide accurate and complete information when registering and keep your account details up to date. You are responsible for maintaining the confidentiality of your password and for all activity under your account. You must notify us promptly of any unauthorised access or use. We are not liable for loss or damage arising from unauthorised use of your account due to your failure to keep your credentials secure.

---

## 3. Use of the Service

You must use the Service only for lawful purposes and in accordance with these Terms. You agree that you will not:

- Use the Service in any way that breaches any Australian or other applicable law, including telemarketing, Do Not Call, and consumer protection laws.
- Use the Service to harass, defame, or mislead any person, or to make unsolicited or unlawful communications.
- Reverse engineer, decompile, or attempt to extract the source code of the Service (except to the extent permitted by law).
- Resell, sublicense, or commercially exploit the Service without our prior written consent.
- Use the Service in a way that could damage, disable, or overburden our systems or networks, or interfere with others' use.
- Scrape, harvest, or collect data from the Service by automated means without our consent.

You are solely responsible for ensuring that your use of the Service (including outbound calls and campaigns) complies with all applicable laws, industry codes, and regulations. We may suspend or terminate your access if we reasonably believe you have breached these Terms or the law.

---

## 4. Call compliance

You are responsible for compliance with all laws and rules that apply to your calls, including the *Do Not Call Register Act 2006* (Cth), the *Spam Act 2003* (Cth), the Australian Consumer Law, and any relevant industry or telemarketing codes. We do not guarantee that the Service will meet every regulatory requirement for your specific use case. You must not use the Service for any purpose that would place us in breach of any law.

---

## 5. Credits, fees and payment

- Use of the Service may require prepaid credits or a subscription. Fees and pricing are as displayed in the Service or as agreed in writing.
- All fees are in Australian dollars (AUD) unless otherwise stated. GST will be applied where we are required to do so.
- Payment is due as specified (e.g. at purchase or on a recurring basis). Failure to pay may result in suspension or termination of your account.
- Refunds are handled in accordance with our refund policy: credits and subscription fees are generally non-refundable except where required by law, and the Australian Consumer Law.
- We may change fees on reasonable notice. Continued use after a fee change constitutes acceptance.
- Stripe (or our nominated payment processor) processes payments. Their terms apply to the payment transaction.

---

## 6. Intellectual property

We (or our licensors) own all rights in the Service, including software, design, text, graphics, and branding. We grant you a limited, non-exclusive, non-transferable licence to access and use the Service for your internal business purposes in accordance with these Terms. You do not acquire any ownership rights. You must not copy, modify, or create derivative works from the Service except as expressly permitted.

---

## 7. Your content and data

You retain ownership of content and data you upload or generate using the Service ("Your Data"). You grant us a licence to use, store, and process Your Data as necessary to provide the Service, improve our systems (where permitted by our Privacy Policy), and comply with law. You are responsible for ensuring that Your Data does not infringe any third-party rights or violate any law. We may remove or refuse to host content that we reasonably consider to breach these Terms or the law.

---

## 8. Confidentiality

Each party will keep the other's confidential information (including business and technical information) confidential and use it only for the purposes of these Terms, except where disclosure is required by law or with the other party's consent.

---

## 9. Disclaimers

To the maximum extent permitted by law:

- The Service is provided "as is" and "as available". We do not warrant that the Service will be uninterrupted, error-free, or free of harmful components.
- We are not responsible for the content or legality of your calls or campaigns, or for any loss arising from your failure to comply with law.
- Nothing in these Terms excludes, restricts, or modifies any consumer guarantee or other right you have under the Australian Consumer Law or other law that cannot be excluded. Where we are permitted to limit our liability, we do so to the fullest extent allowed.

---

## 10. Limitation of liability

- To the fullest extent permitted by law, our total liability to you for any claim arising out of or in connection with the Service or these Terms is limited to the greater of: (a) the amount you paid us in the 12 months before the claim, or (b) AUD 100.
- We are not liable for any indirect, consequential, special, or punitive loss or damage (including loss of profit, revenue, data, or goodwill), whether in contract, tort (including negligence), or otherwise, even if we have been advised of the possibility of such loss.
- These limitations apply except where liability cannot be excluded or limited under the Australian Consumer Law or other applicable law. If you are a "consumer" under the Australian Consumer Law, our liability for a failure to comply with a consumer guarantee is subject to the ACL.

---

## 11. Indemnity

You agree to indemnify and hold us (and our officers, employees, and agents) harmless from and against any claims, losses, damages, costs, and expenses (including reasonable legal fees) arising from: (a) your use of the Service; (b) your breach of these Terms or any law; (c) Your Data; or (d) your violation of any third-party rights.

---

## 12. Termination

- You may stop using the Service and close your account at any time in accordance with our account closure process.
- We may suspend or terminate your account or access to the Service immediately if you breach these Terms, or on reasonable notice for any other reason.
- On termination, your right to use the Service ceases. We may delete or retain Your Data in accordance with our Privacy Policy and data retention practices. Provisions that by their nature should survive (e.g. intellectual property, disclaimers, limitation of liability, indemnity, governing law) will survive termination.

---

## 13. Changes to the Service and Terms

We may change the Service (including features and pricing) with reasonable notice where practicable. We may update these Terms from time to time by posting the revised Terms on voidinc.ai and updating the "Last updated" date. If changes are material, we will notify you by email or through the Service. Your continued use of the Service after the effective date of the changes constitutes acceptance of the new Terms. If you do not agree, you must stop using the Service and may close your account.

---

## 14. General

- **Entire agreement:** These Terms (together with our Privacy Policy and any other documents we incorporate by reference) constitute the entire agreement between you and us regarding the Service.
- **Severability:** If any part of these Terms is held invalid or unenforceable, the rest remains in effect.
- **Waiver:** Our failure to enforce any right does not waive that right.
- **Assignment:** You may not assign your rights or obligations without our written consent. We may assign our rights and obligations in connection with a merger, sale, or restructuring.
- **Governing law and jurisdiction:** These Terms are governed by the laws of Queensland and the Commonwealth of Australia. You submit to the non-exclusive jurisdiction of the courts of Queensland and the Commonwealth of Australia.
- **Contact:** For questions about these Terms, contact us at support@voidinc.ai or at our address below.

---

**Void Inc.**  
24 Sugars Road, Bellbowrie QLD, Australia
`;
